import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useToast } from "@chakra-ui/react";
import apiClient from "utils/apiClient";
import { useTranslation } from "react-i18next";
import i18n from "i18n";

/* ── Icons ── */
const ChevronRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const SaveIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
    <polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
  </svg>
);
const GlobeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="17" height="17">
    <circle cx="12" cy="12" r="10"/>
    <line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
  </svg>
);
const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="17" height="17">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);
const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="17" height="17">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);
const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);
const SendIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <line x1="22" y1="2" x2="11" y2="13"/>
    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
);
const InfoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

/* ── Sub-components ── */
function PasswordInput({ value, onChange, placeholder, id }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <input
        id={id}
        className="lms-input"
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{ paddingRight: 40 }}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", display: "flex", alignItems: "center", padding: 0 }}
      >
        {show ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  );
}

function Field({ label, hint, children, required }) {
  return (
    <div className="lms-form-group">
      <label className="lms-label">
        {label}{required && <span style={{ color: "var(--danger-500)", marginLeft: 2 }}>*</span>}
      </label>
      {children}
      {hint && <div className="lms-field-hint">{hint}</div>}
    </div>
  );
}

function SectionHeader({ icon, title, description }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 14, paddingBottom: 20, borderBottom: "1px solid var(--border-subtle)", marginBottom: 24 }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: "var(--brand-50)", color: "var(--brand-500)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {icon}
      </div>
      <div>
        <div className="t-h2" style={{ marginBottom: 2 }}>{title}</div>
        <div className="t-small lms-text-muted">{description}</div>
      </div>
    </div>
  );
}

/* ── Nav ── */
const TABS = [
  { id: "general",  label: "General",            icon: <GlobeIcon />,  desc: "Info & lokalisasi" },
  { id: "email",    label: "Email / Notifikasi",  icon: <MailIcon />,   desc: "Konfigurasi SMTP" },
  { id: "security", label: "Security",            icon: <ShieldIcon />, desc: "Password & sesi" },
];

export default function SettingsPage() {
  const history = useHistory();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState("general");
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const [general, setGeneral] = useState({
    app_name: "LMS Tenant Management",
    app_description: "Platform pengelolaan tenant sistem LMS EDUNARA",
    timezone: "Asia/Jakarta",
    language: "id",
    support_email: "",
    support_phone: "",
  });

  const [email, setEmail] = useState({
    smtp_host: "",
    smtp_port: "587",
    smtp_user: "",
    smtp_password: "",
    smtp_encryption: "tls",
    from_email: "",
    from_name: "LMS Tenant",
    test_to: "",
  });

  const [security, setSecurity] = useState({
    min_password_length: "8",
    require_uppercase: true,
    require_number: true,
    require_symbol: false,
    session_timeout: "60",
    max_login_attempts: "5",
    lockout_duration: "15",
  });

  useEffect(() => {
    document.title = "Settings | LMS Tenant";
    apiClient.get("/settings").then((res) => {
      if (res.data?.status === "success") {
        const d = res.data.data;
        if (d.general)  setGeneral((p) => ({ ...p, ...d.general }));
        if (d.email)    setEmail((p)   => ({ ...p, ...d.email, test_to: "" }));
        if (d.security) setSecurity((p) => ({ ...p, ...d.security }));
      }
    }).catch(() => {});
  }, []);

  const setG = (k, v) => setGeneral((p) => ({ ...p, [k]: v }));
  const setE = (k, v) => setEmail((p) => ({ ...p, [k]: v }));
  const setS = (k, v) => setSecurity((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put("/settings", { general, email: { ...email, test_to: undefined }, security });
      toast({ title: t("Pengaturan disimpan"), description: t("Perubahan berhasil disimpan."), status: "success", duration: 3000, isClosable: true });
      localStorage.setItem("lms_language", general.language);
      i18n.changeLanguage(general.language);
    } catch (err) {
      toast({ title: t("Gagal menyimpan"), description: err?.response?.data?.message || t("Terjadi kesalahan."), status: "error", duration: 4000, isClosable: true });
    }
    setSaving(false);
  };

  const handleTestEmail = async () => {
    if (!email.test_to) {
      toast({ title: t("Email tujuan kosong"), description: t("Isi email tujuan tes terlebih dahulu."), status: "warning", duration: 3000, isClosable: true });
      return;
    }
    setTesting(true);
    try {
      await apiClient.post("/settings/email/test", { to: email.test_to, smtp: email });
      toast({ title: t("Email terkirim"), description: t("Email tes berhasil dikirim ke {{email}}", { email: email.test_to }), status: "success", duration: 3000, isClosable: true });
    } catch (err) {
      toast({ title: t("Gagal mengirim"), description: err?.response?.data?.message || t("Periksa kembali konfigurasi SMTP."), status: "error", duration: 4000, isClosable: true });
    }
    setTesting(false);
  };

  return (
    <div className="lms-page lms-anim-fade">
      {/* Header */}
      <div className="lms-page-header">
        <div className="lms-page-header-text">
          <div className="lms-breadcrumb">
            <span style={{ cursor: "pointer" }} onClick={() => history.push("/admin/dashboard")}>Dashboard</span>
            <ChevronRightIcon />
            <span className="lms-breadcrumb-current">{t("Settings")}</span>
          </div>
          <div className="t-display" style={{ marginTop: 4 }}>{t("Settings")}</div>
          <div className="t-small lms-text-muted" style={{ marginTop: 2 }}>{t("Kelola konfigurasi sistem")}</div>
        </div>
        <button className="lms-btn lms-btn-primary" onClick={handleSave} disabled={saving}>
          <SaveIcon /> {saving ? t("Menyimpan...") : t("Simpan Perubahan")}
        </button>
      </div>

      {/* Body — sidebar nav + content */}
      <div className="lms-card" style={{ display: "grid", gridTemplateColumns: "220px 1fr", minHeight: 520 }}>

        {/* Left nav */}
        <div style={{ borderRight: "1px solid var(--border-subtle)", padding: "20px 12px", display: "flex", flexDirection: "column", gap: 4 }}>
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "10px 12px", borderRadius: "var(--r-md)", border: "none",
                background: activeTab === tab.id ? "var(--brand-50)" : "transparent",
                color: activeTab === tab.id ? "var(--brand-700)" : "var(--text-base)",
                cursor: "pointer", textAlign: "left", width: "100%", fontFamily: "inherit",
                transition: "all var(--t-fast)",
              }}
              onMouseEnter={(e) => { if (activeTab !== tab.id) e.currentTarget.style.background = "var(--slate-50)"; }}
              onMouseLeave={(e) => { if (activeTab !== tab.id) e.currentTarget.style.background = "transparent"; }}
            >
              <div style={{
                width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: activeTab === tab.id ? "var(--brand-100)" : "var(--slate-100)",
                color: activeTab === tab.id ? "var(--brand-600)" : "var(--text-muted)",
              }}>
                {tab.icon}
              </div>
              <div>
                <div style={{ fontSize: 13.5, fontWeight: activeTab === tab.id ? 600 : 500, lineHeight: 1.3 }}>{t(tab.label)}</div>
                <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 1 }}>{t(tab.desc)}</div>
              </div>
            </button>
          ))}
        </div>

        {/* Right content */}
        <div style={{ padding: "28px 32px" }}>

          {/* ── General ── */}
          {activeTab === "general" && (
            <div>
              <SectionHeader icon={<GlobeIcon />} title={t("General")} description={t("Identitas dan lokalisasi default sistem")} />
              <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>

                <div>
                  <div className="t-eyebrow" style={{ marginBottom: 16 }}>{t("Informasi Aplikasi")}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 20px" }}>
                    <Field label={t("Nama Aplikasi")} required>
                      <input className="lms-input" value={general.app_name} onChange={(e) => setG("app_name", e.target.value)} placeholder="Nama aplikasi" />
                    </Field>
                    <Field label={t("Email Support")} hint={t("Ditampilkan di halaman Help & Support")}>
                      <input className="lms-input" type="email" value={general.support_email} onChange={(e) => setG("support_email", e.target.value)} placeholder="support@domain.com" />
                    </Field>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <Field label={t("Deskripsi Aplikasi")}>
                        <textarea className="lms-textarea" value={general.app_description} onChange={(e) => setG("app_description", e.target.value)} placeholder="Deskripsi singkat aplikasi" rows={3} />
                      </Field>
                    </div>
                  </div>
                </div>

                <div style={{ height: 1, background: "var(--border-subtle)" }} />

                <div>
                  <div className="t-eyebrow" style={{ marginBottom: 16 }}>{t("Lokalisasi")}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 20px" }}>
                    <Field label={t("Zona Waktu")}>
                      <select className="lms-select" value={general.timezone} onChange={(e) => setG("timezone", e.target.value)}>
                        <option value="Asia/Jakarta">WIB — Asia/Jakarta (UTC+7)</option>
                        <option value="Asia/Makassar">WITA — Asia/Makassar (UTC+8)</option>
                        <option value="Asia/Jayapura">WIT — Asia/Jayapura (UTC+9)</option>
                      </select>
                    </Field>
                    <Field label={t("Bahasa Default")}>
                      <select className="lms-select" value={general.language} onChange={(e) => setG("language", e.target.value)}>
                        <option value="id">{t("Bahasa Indonesia")}</option>
                        <option value="en">{t("English")}</option>
                      </select>
                    </Field>
                  </div>
                </div>

              </div>
            </div>
          )}

          {/* ── Email ── */}
          {activeTab === "email" && (
            <div>
              <SectionHeader icon={<MailIcon />} title={t("Email / Notifikasi")} description={t("Konfigurasi server email untuk pengiriman notifikasi sistem")} />
              <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>

                <div>
                  <div className="t-eyebrow" style={{ marginBottom: 16 }}>{t("Konfigurasi SMTP")}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 20px" }}>
                    <Field label={t("SMTP Host")} required>
                      <input className="lms-input" value={email.smtp_host} onChange={(e) => setE("smtp_host", e.target.value)} placeholder="smtp.gmail.com" />
                    </Field>
                    <Field label={t("SMTP Port")} required>
                      <input className="lms-input" type="number" value={email.smtp_port} onChange={(e) => setE("smtp_port", e.target.value)} placeholder="587" />
                    </Field>
                    <Field label={t("Username")} required>
                      <input className="lms-input" value={email.smtp_user} onChange={(e) => setE("smtp_user", e.target.value)} placeholder="user@domain.com" />
                    </Field>
                    <Field label={t("Password")} required>
                      <PasswordInput value={email.smtp_password} onChange={(e) => setE("smtp_password", e.target.value)} placeholder="SMTP password" />
                    </Field>
                    <Field label={t("Enkripsi")}>
                      <select className="lms-select" value={email.smtp_encryption} onChange={(e) => setE("smtp_encryption", e.target.value)}>
                        <option value="tls">TLS</option>
                        <option value="ssl">SSL</option>
                        <option value="none">None</option>
                      </select>
                    </Field>
                  </div>
                </div>

                <div style={{ height: 1, background: "var(--border-subtle)" }} />

                <div>
                  <div className="t-eyebrow" style={{ marginBottom: 16 }}>{t("Identitas Pengirim")}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 20px" }}>
                    <Field label={t("From Email")} required>
                      <input className="lms-input" type="email" value={email.from_email} onChange={(e) => setE("from_email", e.target.value)} placeholder="noreply@domain.com" />
                    </Field>
                    <Field label={t("From Name")}>
                      <input className="lms-input" value={email.from_name} onChange={(e) => setE("from_name", e.target.value)} placeholder="LMS Tenant" />
                    </Field>
                  </div>
                </div>

                <div style={{ height: 1, background: "var(--border-subtle)" }} />

                <div>
                  <div className="t-eyebrow" style={{ marginBottom: 16 }}>{t("Tes Koneksi")}</div>
                  <div style={{ background: "var(--slate-50)", border: "1px solid var(--border-subtle)", borderRadius: "var(--r-lg)", padding: "18px 20px" }}>
                    <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
                      <Field label={t("Email Tujuan Tes")}>
                        <input className="lms-input" type="email" value={email.test_to} onChange={(e) => setE("test_to", e.target.value)} placeholder="admin@domain.com" style={{ minWidth: 260 }} />
                      </Field>
                      <button className="lms-btn lms-btn-secondary" onClick={handleTestEmail} disabled={testing} style={{ marginBottom: 1, whiteSpace: "nowrap" }}>
                        <SendIcon /> {testing ? t("Mengirim...") : t("Kirim Tes")}
                      </button>
                    </div>
                    <div className="t-small lms-text-muted" style={{ marginTop: 6 }}>
                      {t("Kirim email percobaan untuk memverifikasi konfigurasi SMTP di atas sudah benar.")}
                    </div>
                  </div>
                </div>

              </div>
            </div>
          )}

          {/* ── Security ── */}
          {activeTab === "security" && (
            <div>
              <SectionHeader icon={<ShieldIcon />} title={t("Security")} description={t("Aturan password dan kebijakan sesi login")} />
              <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>

                <div>
                  <div className="t-eyebrow" style={{ marginBottom: 16 }}>{t("Kebijakan Password")}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <Field label={t("Panjang Password Minimum")} hint={t("Minimal 6, disarankan 8 atau lebih")}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <input className="lms-input" type="number" min="6" max="32" value={security.min_password_length} onChange={(e) => setS("min_password_length", e.target.value)} style={{ width: 120 }} />
                        <span className="t-small lms-text-muted">{t("karakter")}</span>
                      </div>
                    </Field>
                    <div style={{ background: "var(--slate-50)", border: "1px solid var(--border-subtle)", borderRadius: "var(--r-lg)", padding: "16px 18px", display: "flex", flexDirection: "column", gap: 12 }}>
                      <div className="t-small" style={{ fontWeight: 600, color: "var(--text-strong)", marginBottom: 2 }}>{t("Kompleksitas Password")}</div>
                      {[
                        { key: "require_uppercase", label: "Wajib huruf kapital (A–Z)" },
                        { key: "require_number",    label: "Wajib mengandung angka (0–9)" },
                        { key: "require_symbol",    label: "Wajib mengandung simbol (!@#$…)" },
                      ].map(({ key, label }) => (
                        <label key={key} className="lms-checkbox" style={{ userSelect: "none" }}>
                          <input type="checkbox" checked={security[key]} onChange={(e) => setS(key, e.target.checked)} />
                          <span className="t-small" style={{ color: "var(--text-base)" }}>{t(label)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div style={{ height: 1, background: "var(--border-subtle)" }} />

                <div>
                  <div className="t-eyebrow" style={{ marginBottom: 16 }}>{t("Sesi & Login")}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 20px" }}>
                    <Field label={t("Timeout Sesi")} hint={t("User otomatis logout setelah tidak aktif")}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <input className="lms-input" type="number" min="5" max="1440" value={security.session_timeout} onChange={(e) => setS("session_timeout", e.target.value)} style={{ width: 120 }} />
                        <span className="t-small lms-text-muted">{t("menit")}</span>
                      </div>
                    </Field>
                    <Field label={t("Maks. Percobaan Login")} hint={t("Akun dikunci setelah melebihi batas")}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <input className="lms-input" type="number" min="3" max="20" value={security.max_login_attempts} onChange={(e) => setS("max_login_attempts", e.target.value)} style={{ width: 120 }} />
                        <span className="t-small lms-text-muted">{t("kali")}</span>
                      </div>
                    </Field>
                    <Field label={t("Durasi Lockout")} hint={t("Lama akun dikunci setelah percobaan login melebihi batas")}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <input className="lms-input" type="number" min="1" max="1440" value={security.lockout_duration} onChange={(e) => setS("lockout_duration", e.target.value)} style={{ width: 120 }} />
                        <span className="t-small lms-text-muted">{t("menit")}</span>
                      </div>
                    </Field>
                  </div>
                </div>

                <div className="lms-notice lms-notice-info">
                  <InfoIcon />
                  <span>{t("Perubahan kebijakan password hanya berlaku untuk password yang dibuat setelah pengaturan disimpan. Password lama tidak akan terpengaruh.")}</span>
                </div>

              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
